import { z } from "zod"

import { startOfDayUTC } from "~/utils/datetime"
import {
  type FiscalConfig,
  fiscalYearOf,
  fiscalYearOfRange,
  MS_PER_DAY,
} from "~/utils/fiscalCalendar"

/**
 * The period a NOOS collection's budget is reconciled over: a fiscal year that has a target, or the
 * rolling 52 weeks the plan covers. Ranges are UTC days, half-open, in epoch ms.
 */

export const ROLLING_PERIOD_KEY = "next-52-weeks"
export const AUTO_PERIOD_KEY = "auto"

export type ReconciliationPeriod = { kind: "fy"; year: number } | { kind: "rolling" }

const KEY = /^(?:auto|next-52-weeks|fy(\d{4}))$/

/** The period as the URL spells it: `auto`, `next-52-weeks` or `fy2026`. */
export const reconciliationPeriodKeySchema = z.string().regex(KEY)

/** The key parsed once; `auto` picks the default period, as does one that isn't offered. */
export const reconciliationPeriodSchema = reconciliationPeriodKeySchema.transform(
  (key): ReconciliationPeriod | typeof AUTO_PERIOD_KEY => {
    if (key === ROLLING_PERIOD_KEY) return { kind: "rolling" }
    const year = KEY.exec(key)?.[1]
    return year ? { kind: "fy", year: Number(year) } : AUTO_PERIOD_KEY
  },
)

export const periodKeyOf = (period: ReconciliationPeriod) =>
  period.kind === "rolling" ? ROLLING_PERIOD_KEY : `fy${period.year}`

export const periodLabel = (period: ReconciliationPeriod) =>
  period.kind === "rolling" ? "Next 52 weeks" : `FY${period.year}`

export type DayRange = { from: number; to: number }

type Dated = { startDate: Date; endDate: Date }

/** First day through last, whether the end was saved at the last day's start or its end. */
export const targetDays = (target: Dated): DayRange => ({
  from: startOfDayUTC(target.startDate).getTime(),
  to: startOfDayUTC(target.endDate).getTime() + MS_PER_DAY,
})

const overlapOf = (a: DayRange, b: DayRange): DayRange | null => {
  const from = Math.max(a.from, b.from)
  const to = Math.min(a.to, b.to)
  return from < to ? { from, to } : null
}

const spanOf = (r: DayRange) => r.to - r.from

/** A saved phasing period: `periodStart` as `YYYY-MM-DD`, `weight` its share of the amount. */
export type PhasingPeriod = { periodStart: string; weight: number }

/** A `YYYY-MM-DD` day as its UTC midnight. */
export const utcDayMs = (isoDate: string) => Date.parse(`${isoDate}T00:00:00.000Z`)

/**
 * Each saved period's weight inside `range`, in input order: the weight spread evenly over the
 * period's days, the first period also holding any days before it. Periods outside the target weigh 0.
 */
export function phasingWeightsInRange<P extends PhasingPeriod>(
  target: Dated,
  phasing: P[],
  range: DayRange,
): { period: P; weight: number; weightInRange: number }[] {
  const days = targetDays(target)
  const sorted = [...phasing].sort((a, b) => a.periodStart.localeCompare(b.periodStart))
  return sorted.map((period, i) => {
    const span = overlapOf(days, {
      from: i === 0 ? days.from : utcDayMs(period.periodStart),
      to: i === sorted.length - 1 ? days.to : utcDayMs(sorted[i + 1]!.periodStart),
    })
    if (!span || period.weight <= 0) return { period, weight: 0, weightInRange: 0 }
    const part = overlapOf(span, range)
    return {
      period,
      weight: period.weight,
      weightInRange: part ? (period.weight * spanOf(part)) / spanOf(span) : 0,
    }
  })
}

/**
 * Share (0..1) of a target's amount inside `range`, by its saved phasing (see
 * {@link phasingWeightsInRange}). Without saved phasing every day of the target weighs the same.
 */
export function targetShareInRange(
  target: Dated,
  phasing: PhasingPeriod[],
  range: DayRange,
): number {
  const days = targetDays(target)
  const total = spanOf(days)
  const inside = overlapOf(days, range)
  if (total <= 0 || !inside) return 0
  if (inside.from === days.from && inside.to === days.to) return 1

  let weighted = 0
  let weightTotal = 0
  for (const p of phasingWeightsInRange(target, phasing, range)) {
    weightTotal += p.weight
    weighted += p.weightInRange
  }
  if (weightTotal <= 0) return spanOf(inside) / total
  return weighted / weightTotal
}

export type PeriodSlice<T> = {
  target: T
  /** The target's days inside the period; never overlaps another slice. */
  range: DayRange
  /** Share of the target's amount inside `range`. */
  share: number
  /** Share inside `range` up to the end of the plan horizon: what the plan is compared with. */
  plannedShare: number
}

export type ResolvedPeriod<T> = {
  period: ReconciliationPeriod
  range: DayRange
  /** From this week to the end of the period or the horizon, whichever comes first. */
  planRange: DayRange | null
  /** From the period's start to the start of this week. */
  actualRange: DayRange | null
  slices: PeriodSlice<T>[]
  /** Parts of the period no target covers. */
  uncovered: DayRange[]
}

export type PeriodClock = {
  /** Start of this week, UTC. */
  asOf: number
  /** The day after the plan horizon's last week, UTC. */
  horizonEnd: number
  fiscalConfig: FiscalConfig | null
}

const fiscalYearTargets = <T extends Dated>(
  targets: T[],
  year: number,
  config: FiscalConfig | null,
) => targets.filter((t) => fiscalYearOfRange(t.startDate, t.endDate, config) === year)

const windowOf = (clock: PeriodClock): DayRange => ({ from: clock.asOf, to: clock.horizonEnd })

/**
 * This week's fiscal year and the next, each only when it has a target (the next one also only when
 * the horizon reaches it), then the next 52 weeks when any target falls in them.
 */
export function periodOptions<T extends Dated>(
  targets: T[],
  clock: PeriodClock,
): ReconciliationPeriod[] {
  const window = windowOf(clock)
  const inWindow = (t: T) => overlapOf(targetDays(t), window) != null
  const thisYear = fiscalYearOf(new Date(clock.asOf), clock.fiscalConfig)
  const years = [thisYear, thisYear + 1].filter((year) => {
    const ofYear = fiscalYearTargets(targets, year, clock.fiscalConfig)
    return ofYear.length > 0 && (year === thisYear || ofYear.some(inWindow))
  })
  const options: ReconciliationPeriod[] = years.map((year) => ({ kind: "fy", year }))
  if (targets.some(inWindow)) options.push({ kind: "rolling" })
  return options
}

/** Earlier targets keep their days where two overlap, so no day is counted twice. */
function sliceTargets<T extends Dated>(
  targets: T[],
  range: DayRange,
  clock: PeriodClock,
  phasingOf: (target: T) => PhasingPeriod[],
): PeriodSlice<T>[] {
  const sorted = [...targets].sort((a, b) => targetDays(a).from - targetDays(b).from)
  const slices: PeriodSlice<T>[] = []
  let claimedTo = Number.NEGATIVE_INFINITY
  for (const target of sorted) {
    const inside = overlapOf(targetDays(target), {
      from: Math.max(range.from, claimedTo),
      to: range.to,
    })
    if (!inside) continue
    claimedTo = inside.to
    const phasing = phasingOf(target)
    const planned = overlapOf(inside, { from: Number.NEGATIVE_INFINITY, to: clock.horizonEnd })
    slices.push({
      target,
      range: inside,
      share: targetShareInRange(target, phasing, inside),
      plannedShare: planned ? targetShareInRange(target, phasing, planned) : 0,
    })
  }
  return slices
}

const gapsIn = (range: DayRange, covered: DayRange[]): DayRange[] => {
  const gaps: DayRange[] = []
  let cursor = range.from
  for (const r of covered) {
    if (r.from > cursor) gaps.push({ from: cursor, to: r.from })
    cursor = Math.max(cursor, r.to)
  }
  if (cursor < range.to) gaps.push({ from: cursor, to: range.to })
  return gaps
}

export function resolvePeriod<T extends Dated>(
  targets: T[],
  period: ReconciliationPeriod,
  clock: PeriodClock,
  phasingOf: (target: T) => PhasingPeriod[],
): ResolvedPeriod<T> | null {
  let range: DayRange
  let candidates: T[]
  if (period.kind === "rolling") {
    range = windowOf(clock)
    candidates = targets
  } else {
    candidates = fiscalYearTargets(targets, period.year, clock.fiscalConfig)
    if (candidates.length === 0) return null
    range = candidates.map(targetDays).reduce((a, b) => ({
      from: Math.min(a.from, b.from),
      to: Math.max(a.to, b.to),
    }))
  }
  const slices = sliceTargets(candidates, range, clock, phasingOf)
  if (slices.length === 0) return null
  return {
    period,
    range,
    planRange: overlapOf(range, windowOf(clock)),
    actualRange: overlapOf(range, { from: Number.NEGATIVE_INFINITY, to: clock.asOf }),
    slices,
    uncovered: gapsIn(
      range,
      slices.map((s) => s.range),
    ),
  }
}

/** The asked period when it is offered, otherwise the first option. */
export function pickPeriod(
  asked: ReconciliationPeriod | typeof AUTO_PERIOD_KEY,
  options: ReconciliationPeriod[],
): ReconciliationPeriod | null {
  const match =
    asked === AUTO_PERIOD_KEY
      ? undefined
      : options.find((o) => periodKeyOf(o) === periodKeyOf(asked))
  return match ?? options[0] ?? null
}
